'use client';

import { CaretDown, CaretUp, DotsThree } from '@phosphor-icons/react/ssr';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from '../../lib/utils';
import {
  Table as TablePrimitive,
  TableBody as TableBodyPrimitive,
  TableCaption as TableCaptionPrimitive,
  TableCell as TableCellPrimitive,
  TableFooter as TableFooterPrimitive,
  TableHead as TableHeadPrimitive,
  TableHeader as TableHeaderPrimitive,
  TableRow as TableRowPrimitive,
} from '../ui/table';
import { Avatar } from './avatar';
import { Checkbox } from './checkbox';
import { IconButton } from './icon-button';
import { Menu, MenuContent, MenuTrigger } from './menu';

/** Sort direction of a column, as `aria-sort` names it. */
export type TableSort = 'none' | 'ascending' | 'descending';

export type TableProps = ComponentProps<'table'> & {
  /**
   * `table-layout: fixed`: columns take the widths set on their header cells
   * (Figma sizes every column) and the one without a width fills the rest.
   * Needed for text cells to truncate with an ellipsis.
   */
  fixed?: boolean;
  /**
   * Keep the header row in view while the body scrolls. The container is the
   * scroller, so give it a height (`containerClassName="max-h-[480px]"`).
   */
  stickyHeader?: boolean;
  /** Class for the scrolling container around the `<table>`. */
  containerClassName?: string;
};

/**
 * Data table (Figma: Components ↳ Grid & Table, 38:166): semantic
 * `<table>` / `<thead>` / `<tbody>` built from Table Header Cells and Table
 * Cells inside 56px Table Rows. Compose `Table` › `TableHeader` › `TableRow` ›
 * `TableHead`, and `TableBody` › `TableRow` › `TableCell`. Name it with a
 * `TableCaption` (may be `sr-only`) or `aria-label`. For sorting and row
 * selection state out of the box, use `DataTable`.
 */
export function Table({ fixed, stickyHeader, className, ...props }: TableProps) {
  return (
    <TablePrimitive
      data-sticky-header={stickyHeader || undefined}
      className={cn(
        fixed && 'table-fixed',
        stickyHeader && '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-1',
        className,
      )}
      {...props}
    />
  );
}

export const TableHeader = TableHeaderPrimitive;
export const TableBody = TableBodyPrimitive;
export const TableFooter = TableFooterPrimitive;
export const TableCaption = TableCaptionPrimitive;

export type TableRowProps = ComponentProps<'tr'> & {
  /** Figma State=Selected: brand/subtle fill (pair with a checked select cell). */
  selected?: boolean;
};

/**
 * Figma Table Row (58:96): Default bg/surface, Hover bg/subtle, Selected
 * brand/subtle. Keep 56px rows and the cell padding.
 */
export function TableRow({ selected, className, ...props }: TableRowProps) {
  return (
    <TableRowPrimitive
      data-selected={selected || undefined}
      className={cn('group/row', className)}
      {...props}
    />
  );
}

export type TableHeadProps = Omit<ComponentProps<'th'>, 'align'> & {
  /** Figma "Align". Right-align numeric columns (and their cells). */
  align?: 'left' | 'right';
  /**
   * Figma "Sort". Set it (even to `'none'`) to make the column sortable: the
   * label becomes a button and the header cell carries `aria-sort` while the
   * column is sorted.
   */
  sort?: TableSort;
  /** Called when the sort button is pressed; the caller picks the next direction. */
  onSort?: () => void;
  /** Column width in px (or any CSS length), as Figma sizes it. */
  width?: number | string;
};

const sortIcon = 'size-3 shrink-0';

/**
 * Figma Table Header Cell (57:27): 40 tall, Label/Small. Unsorted columns are
 * text/secondary and reveal a faint caret on hover / focus; the sorted column
 * is text/primary with an icon/primary caret (up = ascending). Right-aligned
 * cells put the caret before the label.
 */
export function TableHead({
  align = 'left',
  sort,
  onSort,
  width,
  className,
  style,
  children,
  ...props
}: TableHeadProps) {
  const sortable = sort !== undefined;
  const sorted = sort === 'ascending' || sort === 'descending';
  const Icon = sort === 'descending' ? CaretDown : CaretUp;
  const icon = sortable && (
    <Icon
      aria-hidden
      data-slot="table-sort-icon"
      className={cn(
        sortIcon,
        sorted
          ? 'text-icon-primary'
          : 'text-icon-tertiary hidden group-hover/sort:block group-focus-visible/sort:block',
      )}
    />
  );
  return (
    <TableHeadPrimitive
      scope="col"
      aria-sort={sorted ? sort : undefined}
      data-align={align}
      data-sorted={sorted || undefined}
      style={width !== undefined ? { width, ...style } : style}
      className={cn(
        align === 'right' && 'text-right',
        sorted && 'text-text-primary',
        className,
      )}
      {...props}
    >
      {sortable ? (
        <button
          type="button"
          onClick={onSort}
          className={cn(
            'group/sort -my-1 inline-flex max-w-full cursor-pointer items-center gap-1 rounded-xs py-1 align-middle outline-none',
            'focus-visible:ring-2 focus-visible:ring-border-focus',
          )}
        >
          {align === 'right' && icon}
          <span data-slot="table-head-label" className="truncate">
            {children}
          </span>
          {align === 'left' && icon}
        </button>
      ) : (
        children
      )}
    </TableHeadPrimitive>
  );
}

export type TableCellProps = Omit<ComponentProps<'td'>, 'align'> & {
  /**
   * Figma "Type" for plain cells. `text` (default) truncates with an ellipsis;
   * `numeric` is Data/Medium with tabular figures, right-aligned; `actions` is
   * the 64px column holding a `TableRowActions`. Stacked, User, Status and
   * Checkbox are content (`TableCellStack`, `TableCellUser`, a `Badge`,
   * `TableSelectCell`).
   */
  variant?: 'text' | 'numeric' | 'actions';
};

export function TableCell({
  variant = 'text',
  className,
  children,
  ...props
}: TableCellProps) {
  // Text runs truncate in a block; anything else (a Badge, a stack, a
  // button) is centred in a flex row - inline boxes in a table cell sit on
  // the text baseline, a few px off Figma's centre.
  const plain = typeof children === 'string' || typeof children === 'number';
  return (
    <TableCellPrimitive
      data-variant={variant}
      className={cn('max-w-0', variant === 'actions' && 'w-16 max-w-none', className)}
      {...props}
    >
      <div
        data-slot="table-cell-content"
        className={cn(
          plain || variant === 'numeric' ? 'truncate' : 'flex min-w-0 items-center gap-2',
          variant === 'numeric' && 'text-data-md text-right tabular-nums',
          variant === 'actions' && 'justify-end',
        )}
      >
        {children}
      </div>
    </TableCellPrimitive>
  );
}

/**
 * Figma Table Cell Type=Stacked: Body/Medium Strong primary line over a
 * Caption text/tertiary line, both truncating. On a Selected row the caption
 * steps up to text/secondary (text/tertiary on brand/subtle fails contrast).
 */
export function TableCellStack({
  primary,
  secondary,
  className,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
  className?: string;
}) {
  return (
    <div data-slot="table-cell-stack" className={cn('flex min-w-0 flex-col', className)}>
      <span data-slot="table-cell-primary" className="text-body-md-strong truncate">
        {primary}
      </span>
      {secondary && (
        <span
          data-slot="table-cell-secondary"
          className="text-caption text-text-tertiary group-data-selected/row:text-text-secondary truncate"
        >
          {secondary}
        </span>
      )}
    </div>
  );
}

/**
 * Figma Table Cell Type=User: a 24px Avatar (photo or initials) and the name,
 * 8px apart. The avatar is decorative here - the name is the text.
 */
export function TableCellUser({
  name,
  src,
  initials,
  className,
}: {
  name: string;
  src?: string;
  initials?: string;
  className?: string;
}) {
  return (
    <div
      data-slot="table-cell-user"
      className={cn('flex min-w-0 items-center gap-2', className)}
    >
      <Avatar size={24} name={name} src={src} initials={initials} alt="" />
      <span className="truncate">{name}</span>
    </div>
  );
}

/**
 * Figma Table Cell Type=Checkbox (52 wide): selects one row. `label` names the
 * row ("Select Product teaser — v3").
 */
export function TableSelectCell({
  label,
  checked,
  onCheckedChange,
  disabled,
  className,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <TableCellPrimitive data-variant="checkbox" className={cn('w-13 pr-0', className)}>
      <div data-slot="table-cell-content" className="flex items-center">
        <Checkbox
          aria-label={label}
          checked={checked}
          disabled={disabled}
          onCheckedChange={(value) => onCheckedChange(value === true)}
        />
      </div>
    </TableCellPrimitive>
  );
}

/**
 * The header's select-all box: checked when every row is selected,
 * indeterminate when some are. Pressing it selects all, or clears all.
 */
export function TableSelectAllHead({
  label = 'Select all rows',
  checked,
  onCheckedChange,
  disabled,
  className,
}: {
  label?: string;
  checked: boolean | 'indeterminate';
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <TableHeadPrimitive
      scope="col"
      data-variant="checkbox"
      className={cn('w-13 pr-0', className)}
    >
      <div className="flex items-center">
        <Checkbox
          aria-label={label}
          checked={checked}
          disabled={disabled}
          // From indeterminate, a press selects everything.
          onCheckedChange={() => onCheckedChange(checked !== true)}
        />
      </div>
    </TableHeadPrimitive>
  );
}

/**
 * Figma Table Cell Type=Actions: a Ghost Small Icon Button (dots-three,
 * icon/secondary) opening a Menu of row actions. Render inside
 * `<TableCell variant="actions">`; `label` names the button
 * ("Actions for Product teaser — v3").
 */
export function TableRowActions({
  label,
  children,
}: {
  label: string;
  /** MenuItems. */
  children: ReactNode;
}) {
  return (
    <Menu>
      <MenuTrigger asChild>
        <IconButton icon={DotsThree} size="sm" aria-label={label} />
      </MenuTrigger>
      <MenuContent align="end">{children}</MenuContent>
    </Menu>
  );
}
